<script setup lang="ts">
import { ref } from "vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { resetDrawerWidth, setDrawerWidth } from "@/stores/DrawerWidthState";
import { effectiveDrawerWidth } from "@/stores/CanvasLayoutState";
import { getCanvasTextScale } from "@/composable/useCanvasUiScale";

/**
 * Poignée de redimensionnement du volet, sur son bord gauche. Toujours visible
 * (une languette avec des points de préhension) : sur tablette il n'y a pas de
 * survol pour révéler un simple filet. Le volet est ancré à droite : tirer
 * vers la gauche l'élargit. Double-clic : largeur par défaut.
 */
const emit = defineEmits<{ (e: "resizing", value: boolean): void }>();

const dragging = ref(false);
let startX = 0;
let startWidth = 0;

function onPointerDown(event: PointerEvent) {
  startX = event.clientX;
  // Largeur affichée (éventuellement bornée à l'écran) : le bord suit le doigt.
  startWidth = effectiveDrawerWidth.value;
  (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  dragging.value = true;
  emit("resizing", true);
}

function onPointerMove(event: PointerEvent) {
  if (!dragging.value) return;
  // Pointeur en px écran, largeur du volet en px de base (volet zoomé comme
  // les textes).
  setDrawerWidth(startWidth + (startX - event.clientX) / getCanvasTextScale());
}

function onPointerEnd(event: PointerEvent) {
  if (!dragging.value) return;
  const el = event.currentTarget as HTMLElement;
  if (el.hasPointerCapture(event.pointerId)) {
    el.releasePointerCapture(event.pointerId);
  }
  dragging.value = false;
  emit("resizing", false);
}
</script>

<template>
  <div
    class="drawer-resize-handle"
    :class="{ dragging }"
    role="separator"
    aria-orientation="vertical"
    :aria-label="$t('canvas:drawers.resize')"
    @pointerdown.stop.prevent="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerEnd"
    @pointercancel="onPointerEnd"
    @dblclick="resetDrawerWidth()"
  >
    <span class="drawer-resize-grip">
      <PhosphorIcon name="dots-six-vertical" :size="14" weight="bold" />
    </span>
  </div>
</template>

<style lang="scss" scoped>
// Zone tactile à cheval sur le bord gauche du volet : 12 px côté plan (les
// bulles s'en écartent d'autant, cf. DRAWER_HANDLE_OVERHANG), 8 px côté volet
// (au-delà, elle mordrait sur le bouton de fermeture).
.drawer-resize-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  left: -12px;
  width: 20px;
  cursor: col-resize;
  touch-action: none;
  z-index: 2;

  // Le bord du volet s'allume pendant le geste.
  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 11px;
    width: 2px;
    background: transparent;
    transition: background-color 0.15s ease;
  }

  &.dragging::after {
    background: var(--canvas-btn-active-bg);
  }

  // Survol à la souris seulement : au doigt, :hover reste collé après le
  // relâchement et ferait croire que le bord est encore saisi.
  @media (hover: hover) and (pointer: fine) {
    &:hover::after {
      background: var(--canvas-btn-active-bg);
    }
  }
}

// Languette toujours visible, centrée sur le bord : ce qui se saisit.
.drawer-resize-grip {
  position: absolute;
  top: 50%;
  left: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 56px;
  border: 1px solid var(--canvas-border);
  border-radius: 8px;
  background-color: var(--canvas-bg);
  color: rgba(255, 255, 255, 0.6);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  transform: translate(-50%, -50%);
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;
  z-index: 1;

  .drawer-resize-handle.dragging & {
    border-color: var(--canvas-btn-active-bg);
    background-color: var(--canvas-btn-active-bg);
    color: #1a1a1a;
  }

  @media (hover: hover) and (pointer: fine) {
    .drawer-resize-handle:hover & {
      border-color: var(--canvas-btn-active-bg);
      background-color: var(--canvas-btn-active-bg);
      color: #1a1a1a;
    }
  }
}
</style>
